import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, toGridSortField, toODataSortField,
} from '../listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from '../ui';
import { useC360List, C360Table, ColumnChooser } from '../c360';
import { usePageMeta } from '../../hooks/usePageMeta';
import { EDocumentKpiStrip } from './EDocumentKpiStrip';
import { EDocumentDrawer } from './EDocumentDrawer';
import { EDocumentBulkBar } from './EDocumentBulkBar';
import { EDocumentReconciliationModal } from './EDocumentReconciliationModal';
import {
  DOC_TYPE_OPTIONS, DOC_TYPE_LABEL,
  SCENARIO_OPTIONS, SCENARIO_LABEL,
  SOURCE_TYPE_OPTIONS, SOURCE_TYPE_LABEL,
  TRANSMISSION_OPTIONS, GIB_OPTIONS, RESPONSE_OPTIONS,
  transmissionBadge, gibBadge, responseBadge,
  deriveOverallStatus, remainingDays, isApplicable,
  fmtDate, fmtMoney,
} from './eDocumentModel';
import './eDocument.css';

const API_BASE = '/api/edocuments';

/**
 * ══ ÜÇ EKSEN = ÜÇ AYRI KOLON + ÜÇ AYRI FİLTRE (spec §1/§6.2) ══════════════════════════════════
 * Durum alanları backend'de `int`tir → c360'ın `select` filtresi onları düz OData `eq`'e çevirir
 * (filterModel.js → buildFilterClause). Yani üç ayrı eksen filtresi HİÇ YENİ ALTYAPI GEREKTİRMEDEN
 * çalışır. Tek bir "Durum" dropdown'ı, "gönderilmiş AMA zarfı hatalı" kesişimini SORGULANAMAZ
 * hâle getirirdi.
 *
 * ⚠ `overall` (Genel Durum) BURADA YOK — çünkü TÜRETİLMİŞTİR ve SAKLANMAZ (iş kuralı 2). Var olmayan
 * bir kolona OData filtresi kurulamaz; kullanıcı zaten üç eksenin kendisinden filtreler, rozet
 * yalnız TARAMA içindir.
 */
const COLUMN_FILTER_META = {
  documentType: { type: 'select', odataField: 'DocumentType', options: DOC_TYPE_OPTIONS },
  scenario: { type: 'select', odataField: 'Scenario', options: SCENARIO_OPTIONS },
  documentNo: { type: 'text', odataField: 'DocumentNo' },
  issuedAt: { type: 'date', odataField: 'IssuedAt' },
  sourceType: { type: 'select', odataField: 'SourceType', options: SOURCE_TYPE_OPTIONS },
  taxNumber: { type: 'text', odataField: 'TaxNumber' },
  netAmount: { type: 'number', odataField: 'NetAmount' },
  // ── ÜÇ EKSEN ──
  transmissionStatus: { type: 'select', odataField: 'TransmissionStatus', options: TRANSMISSION_OPTIONS },
  gibStatus: { type: 'select', odataField: 'GibStatus', options: GIB_OPTIONS },
  responseStatus: { type: 'select', odataField: 'ResponseStatus', options: RESPONSE_OPTIONS },
  lastErrorMessage: { type: 'text', odataField: 'LastErrorMessage' },
};

const SEARCH_FIELDS = ['DocumentNo', 'PrintedNo', 'TaxNumber'];

/**
 * KPI kartı → OData baseFilter. Her kart bir FİLTRE KISAYOLUDUR (spec §6.1).
 * `baseFilter` kullanıcı filtreleriyle VE'lenir ve kullanıcı onu temizleyemez — kartı tekrar
 * tıklamak kaldırır (EDocumentKpiStrip toggle eder).
 */
function kpiToBaseFilter(key) {
  if (!key) return '';
  const soon = new Date();
  soon.setDate(soon.getDate() + 3);
  const soonIso = soon.toISOString();

  switch (key) {
    case 'queued': return 'TransmissionStatus eq 0';
    case 'transmissionFailed': return 'TransmissionStatus eq 3';
    case 'envelopeError': return 'GibStatus eq 3';
    case 'awaitingResponse': return 'ResponseStatus eq 1';
    // "≤ 3 gün kaldı" — süresi DOLMUŞ olanlar da bu kümededir (deadline job günlük koşar; ekran
    // gerçeği gösterir ve job'ın yetişmesini beklemez).
    case 'deadlineSoon':
      return `ResponseStatus eq 1 and ResponseDeadlineAt ne null and ResponseDeadlineAt le ${soonIso}`;
    default: return '';
  }
}

export function EDocumentControlPage() {
  usePageMeta('E-Belge Kontrol', 'e-Fatura / e-Arşiv / e-İrsaliye belgelerinin GİB bağlantısını izleyin.');

  const [toast, setToast] = useState(null);
  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const [summary, setSummary] = useState(null);
  const [activeKpi, setActiveKpi] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [reconOpen, setReconOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // ══ SEÇİM STATE'İ LOKALDİR ═══════════════════════════════════════════════════════════════════
  // c360'ta seçim diye bir kavram YOK. Onu paylaşılan altyapıya eklemek, bu ekran için tüm liste
  // ekranlarını riske atmak olurdu. Bunun yerine seçim BURADA tutuluyor ve checkbox ile `İncele`
  // birer columnDef olarak ekleniyor → PAYLAŞILAN c360'a SIFIR DOKUNUŞ.
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // ══ DRAWER — SEÇİMDEN TAMAMEN BAĞIMSIZ (iş kuralı 8) ═════════════════════════════════════════
  // Ayrı bir state: bir satırın checkbox'ını işaretlemek onu AÇMAZ, başka bir satırı `İncele` ile
  // açmak seçimi BOZMAZ. İkisi farklı sorulara cevap verir.
  const [drawerId, setDrawerId] = useState(null);

  const baseFilter = useMemo(() => kpiToBaseFilter(activeKpi), [activeKpi]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    // ══ VARSAYILAN SIRALAMA: KALAN SÜRE (spec §4) ═══════════════════════════════════════════════
    // "Yanıt bekleniyor" tek başına bilgi değildir — bilgi KAÇ GÜN KALDIĞIDIR. Ekran açıldığında
    // en acil belge (süresi dolmak üzere olan) EN ÜSTTE olmalıdır. Alfabetik veya tarih sıralaması,
    // 8 günlük yasal süreyi kaçırmanın en kolay yoludur.
    defaultSortField: 'ResponseDeadlineAt',
    defaultSortDir: 'asc',
    baseFilter,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData,
  } = list;

  // ── KPI özeti (5 sayı tek sorguda) ─────────────────────────────────────────────────────────
  const loadSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/summary`, { headers: authHeaders() });
      if (!res.ok) return;
      const body = await res.json();
      setSummary(body?.data ?? body?.Data ?? null);
    } catch { /* KPI şeridi kritik değil — hata listeyi engellemez */ }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const refreshAll = useCallback(() => { fetchData(); loadSummary(); }, [fetchData, loadSummary]);

  // ── Ad çözümleme (Plasiyer / Müşteri / Cari) ────────────────────────────────────────────────
  // EDocument yalnız FK taşır (isim taşımaz — denormalize etseydik müşteri adı değiştiğinde belge
  // eski adı gösterirdi). Temsilci/cari listeleri KÜÇÜKTÜR → bir kez çekilir.
  // Müşteri listesi BÜYÜK olabilir → SAYFADAKİ satırların id'leri kadarını çekip cache'liyoruz.
  const [repMap, setRepMap] = useState({});
  const [vendorMap, setVendorMap] = useState({});
  const [customerMap, setCustomerMap] = useState({});
  const customerFetchRef = useRef(new Set());

  useEffect(() => {
    (async () => {
      try {
        const [rRes, vRes] = await Promise.all([
          fetch('/api/representatives?$select=Id,Name&$top=1000', { headers: authHeaders() }),
          fetch('/api/vendors?$select=Id,Name&$top=1000', { headers: authHeaders() }),
        ]);
        if (rRes.ok) {
          const b = await rRes.json();
          setRepMap(Object.fromEntries((b?.value ?? []).map((x) => [x.id ?? x.Id, x.name ?? x.Name])));
        }
        if (vRes.ok) {
          const b = await vRes.json();
          setVendorMap(Object.fromEntries((b?.value ?? []).map((x) => [x.id ?? x.Id, x.name ?? x.Name])));
        }
      } catch { /* lookup yoksa kolonlar id gösterir; liste yine çalışır */ }
    })();
  }, []);

  // Yalnız GÖRÜNEN satırların müşterilerini çöz — tüm müşteri tablosunu çekmek, binlerce kayıtlı
  // bir tenant'ta ekranı açılışta dakikalarca bekletirdi.
  useEffect(() => {
    const missing = [...new Set(
      rows.map((r) => r.customerId).filter((id) => id && !customerMap[id] && !customerFetchRef.current.has(id)),
    )];
    if (missing.length === 0) return;
    missing.forEach((id) => customerFetchRef.current.add(id));

    (async () => {
      try {
        const clause = missing.map((id) => `Id eq ${id}`).join(' or ');
        const url = `/api/customers?$select=Id,Title&$top=${missing.length}&$filter=${encodeURIComponent(clause)}`;
        const res = await fetch(url, { headers: authHeaders() });
        if (!res.ok) return;
        const b = await res.json();
        const next = Object.fromEntries((b?.value ?? []).map((x) => [x.id ?? x.Id, x.title ?? x.Title]));
        setCustomerMap((prev) => ({ ...prev, ...next }));
      } catch { /* ad çözülemezse hücre '—' gösterir; belge izleme etkilenmez */ }
    })();
  }, [rows, customerMap]);

  // ── Seçim ───────────────────────────────────────────────────────────────────────────────────
  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const allVisibleSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const everySelected = rows.length > 0 && rows.every((r) => next.has(r.id));
      rows.forEach((r) => { if (everySelected) next.delete(r.id); else next.add(r.id); });
      return next;
    });
  }, [rows]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const selectionCount = selectedIds.size;

  // ── Toplu aksiyonlar — SESSİZ DAVRANMAZ (iş kuralı 9) ──────────────────────────────────────
  const runBulk = useCallback(async (path, verb) => {
    setBulkBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${path}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ids: [...selectedIds] }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Toplu işlem başarısız');

      const d = body?.data ?? body?.Data ?? {};

      // ══ ATLANANLARI GEREKÇESİYLE SÖYLÜYORUZ ═══════════════════════════════════════════════
      // "12 belge kuyruğa alındı" tek başına YALAN olurdu: 3 belge sessizce atlandıysa kullanıcı
      // onların da gittiğini sanır. Sessizce hepsini göndermek ise GİB'de MÜKERRER BELGE demektir.
      const parts = [`${d.queued ?? 0} belge ${verb}`];
      if (d.skipped > 0) {
        const reasons = Object.entries(d.skipReasons || {})
          .map(([reason, n]) => `${n} ${reason.toLowerCase()}`)
          .join(', ');
        parts.push(`${d.skipped} belge atlandı (${reasons})`);
      }
      let msg = parts.join(' · ');

      // Zarf hatası olanlar kuyruğa alındıysa AYRICA uyar: belge düzeltilmediyse aynı hatayı verirler.
      if (d.envelopeErrorQueued > 0) {
        msg += ` — UYARI: ${d.envelopeErrorQueued} belge ZARF HATASIYDI; içerik düzeltilmediyse aynı hatayı verecek.`;
      }
      if (d.integratorConfigured === false) {
        msg += ' (Entegratör yapılandırılmamış — belgeler kuyrukta bekleyecek.)';
      }

      showToast(d.skipped > 0 || d.envelopeErrorQueued > 0 ? 'info' : 'success', msg);
      clearSelection();
      refreshAll();
    } catch (err) {
      showToast('error', err.message || 'Toplu işlem başarısız');
    } finally {
      setBulkBusy(false);
    }
  }, [selectedIds, showToast, clearSelection, refreshAll]);

  // ── Kolonlar ────────────────────────────────────────────────────────────────────────────────
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    // Checkbox — SEÇİM (toplu aksiyon). Detay AÇMAZ (iş kuralı 8).
    columnHelper.display({
      id: 'select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <input
          type="checkbox"
          checked={allVisibleSelected}
          onChange={toggleAll}
          onClick={(e) => e.stopPropagation()}
          aria-label="Tümünü seç"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.original.id)}
          onChange={() => toggleRow(row.original.id)}
          onClick={(e) => e.stopPropagation()}
          aria-label="Satırı seç"
        />
      ),
    }),

    // GENEL DURUM — TÜRETİLMİŞ (iş kuralı 2). Sıralanamaz/filtrelenemez: backend'de böyle bir kolon
    // YOKTUR. Tarama içindir; kullanıcı gerçek filtreyi üç eksenden kurar.
    columnHelper.display({
      id: 'overall',
      header: 'Genel Durum',
      enableSorting: false,
      cell: ({ row }) => {
        const st = deriveOverallStatus(row.original);
        return <StatusBadge tone={st.tone}>{st.label}</StatusBadge>;
      },
    }),

    columnHelper.accessor('documentType', {
      header: 'Belge Tipi', meta: { filterType: 'select' },
      cell: (info) => DOC_TYPE_LABEL[info.getValue()] || '—',
    }),

    // SENARYO yalnız e-Faturada vardır. e-Arşiv/e-İrsaliyede `—` (boş değil, UYGULANAMAZ).
    columnHelper.accessor('scenario', {
      header: 'Senaryo', meta: { filterType: 'select' },
      cell: ({ row }) => {
        const r = row.original;
        if (!isApplicable('scenario', r.documentType, r.scenario) || r.scenario == null) {
          return <span className="edoc-na">—</span>;
        }
        return SCENARIO_LABEL[r.scenario] || '—';
      },
    }),

    columnHelper.accessor('documentNo', {
      header: 'Belge No', meta: { filterType: 'text' },
      cell: (info) => <span className="edoc-mono">{info.getValue() || '—'}</span>,
    }),

    columnHelper.accessor('issuedAt', {
      header: 'Tarih', meta: { filterType: 'date' },
      cell: (info) => fmtDate(info.getValue()),
    }),

    columnHelper.accessor('sourceType', {
      header: 'Hareket Tipi', meta: { filterType: 'select' },
      cell: (info) => SOURCE_TYPE_LABEL[info.getValue()] || '—',
    }),

    columnHelper.display({
      id: 'representative',
      header: 'Plasiyer',
      enableSorting: false,
      cell: ({ row }) => repMap[row.original.userId] || <span className="edoc-na">—</span>,
    }),

    columnHelper.display({
      id: 'customer',
      header: 'Müşteri',
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        // Satıcıya iadede alıcı VENDOR'dur (müşteri değil) — doğru cariyi gösteriyoruz.
        const name = customerMap[r.customerId] || vendorMap[r.vendorId];
        return name || <span className="edoc-na">—</span>;
      },
    }),

    columnHelper.accessor('taxNumber', {
      header: 'VKN', meta: { filterType: 'text' },
      cell: (info) => info.getValue()
        ? <span className="edoc-mono">{info.getValue()}</span>
        : <span className="edoc-na">—</span>,
    }),

    columnHelper.accessor('netAmount', {
      header: 'Net Tutar', meta: { filterType: 'number' },
      cell: (info) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(info.getValue())}</span>,
    }),

    // ══════════════════════════════ ÜÇ EKSEN — ÜÇ AYRI KOLON ══════════════════════════════════
    // Tek rozette birleştirmek "kimi arayacağım" sorusunu ekrandan silerdi (spec §1).

    // EKSEN 1 — SERVİS. Hata: bağlantı/timeout. Çözen: BİT. Tekrar denemek İŞE YARAR.
    columnHelper.accessor('transmissionStatus', {
      header: 'Servis', meta: { filterType: 'select' },
      cell: ({ row }) => {
        const b = transmissionBadge(row.original);
        return b ? <StatusBadge tone={b.tone}>{b.label}</StatusBadge> : <span className="edoc-na">—</span>;
      },
    }),

    // EKSEN 2 — GİB ZARF = BELGE İÇERİĞİ. Çözen: MUHASEBE. Tekrar denemek İŞE YARAMAZ.
    columnHelper.accessor('gibStatus', {
      header: 'GİB Zarf', meta: { filterType: 'select' },
      cell: ({ row }) => {
        const b = gibBadge(row.original);
        return b ? <StatusBadge tone={b.tone}>{b.label}</StatusBadge> : <span className="edoc-na">—</span>;
      },
    }),

    // EKSEN 3 — KARŞI TARAF YANITI. Uygulanamazsa `—` (İŞ KURALI 4: SARI ROZET GÖSTERİLMEZ,
    // yanlış alarmdır — temel senaryo faturasında yanıt DİYE BİR ŞEY YOKTUR).
    columnHelper.accessor('responseStatus', {
      header: 'Yanıt', meta: { filterType: 'select' },
      cell: ({ row }) => {
        const b = responseBadge(row.original);
        return b ? <StatusBadge tone={b.tone}>{b.label}</StatusBadge> : <span className="edoc-na">—</span>;
      },
    }),

    // KALAN SÜRE — GRID'İN VARSAYILAN SIRALAMASI (spec §4).
    // >3g nötr · ≤3g sarı · ≤1g KIRMIZI · süre doldu → nötr (aksiyon yok).
    columnHelper.accessor('responseDeadlineAt', {
      header: 'Kalan Süre',
      enableColumnFilter: false,
      cell: ({ row }) => {
        const r = remainingDays(row.original);
        if (!r.applicable) return <span className="edoc-na">—</span>;
        return <span className={`edoc-remaining edoc-remaining--${r.tone}`}>{r.label}</span>;
      },
    }),

    // Otomatik ve MANUEL deneme AYRI (spec §11.7): kullanıcı "5 deneme"yi gördüğünde bunun sistemin
    // ısrarı mı yoksa kendi müdahalesi mi olduğunu bilmek zorundadır.
    columnHelper.display({
      id: 'attempts',
      header: 'Deneme',
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        const auto = r.transmissionAttempts ?? 0;
        const manual = r.manualAttempts ?? 0;
        if (auto === 0 && manual === 0) return <span className="edoc-na">0</span>;
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }} title={`${auto} otomatik · ${manual} manuel`}>
            {auto}{manual > 0 && <span className="edoc-danger"> +{manual}m</span>}
          </span>
        );
      },
    }),

    columnHelper.accessor('lastErrorMessage', {
      header: 'Son Hata', meta: { filterType: 'text' },
      cell: (info) => {
        const v = info.getValue();
        if (!v) return <span className="edoc-na">—</span>;
        return (
          <span className="edoc-danger" title={v} style={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {v}
          </span>
        );
      },
    }),

    // ══ SATIR SONU `İncele` — DETAYIN EN DOĞRUDAN YOLU (spec §6.4) ═══════════════════════════
    // Checkbox seçimlerinden BAĞIMSIZDIR: kullanıcı 12 belge seçmişken bile tek bir satırı
    // inceleyebilir ve seçimi bozulmaz.
    columnHelper.display({
      id: 'inspect',
      header: '',
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <button
          type="button"
          className="edoc-rowbtn"
          onClick={(e) => { e.stopPropagation(); setDrawerId(row.original.id); }}
          title="Belgeyi incele"
        >
          İncele
        </button>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [selectedIds, allVisibleSelected, toggleAll, toggleRow, repMap, customerMap, vendorMap]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('ResponseDeadlineAt', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  // ══ ARAÇ ÇUBUĞU `İncele` — YALNIZ TAM 1 SEÇİMDE AKTİF (spec §6.4) ═════════════════════════════
  // 0 veya N seçimde SOLUK durur ve SEBEBİNİ söyler. Tıklatıp sonra modal uyarı vermek, butonu
  // baştan soluk göstermekten KÖTÜDÜR — kullanıcıyı boşuna yürütür.
  const canInspect = selectionCount === 1;
  const inspectTitle = selectionCount === 0
    ? 'İncelemek için tek belge seçin.'
    : canInspect
      ? 'Seçili belgeyi incele'
      : `${selectionCount} belge seçili — incelemek için tek belge seçin.`;

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Belge no / matbu no / VKN ile ara..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && (
        <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>
          Temizle
        </button>
      )}
      <button style={s.btnSecondary} onClick={refreshAll}>Yenile</button>

      <button
        type="button"
        style={{ ...s.btnSecondary, opacity: canInspect ? 1 : 0.4, cursor: canInspect ? 'pointer' : 'not-allowed' }}
        onClick={canInspect ? () => setDrawerId([...selectedIds][0]) : undefined}
        disabled={!canInspect}
        title={inspectTitle}
      >
        İncele
      </button>

      {/* Mutabakat SEÇİM GEREKTİRMEZ (spec §6.6) — ayrı bir çalışma modudur. */}
      <button type="button" style={s.btnSecondary} onClick={() => setReconOpen(true)}>
        Mutabakat
      </button>

      <ColumnChooser table={table} />

      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        {/* Entegratör durumu DÜRÜSTÇE gösterilir: yapılandırılmamışsa belgelerin niçin kuyrukta
            beklediği ancak böyle anlaşılır. Sessizce boş bir ekran, "sistem bozuk" gibi okunur. */}
        {summary && (
          <span className="edoc-muted" title="E-belge entegratörü">
            {summary.integratorConfigured
              ? `${summary.integrator} entegratörü`
              : 'Entegratör yapılandırılmamış'}
          </span>
        )}
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel'e Aktar"
          onClick={() => showToast('info', 'Excel aktarımı için filtreyi daraltıp tekrar deneyin.')}
        >
          <ExcelIcon size={16} />
        </button>
      </span>
    </>
  );

  const footerNode = (
    <>
      <EDocumentBulkBar
        count={selectionCount}
        busy={bulkBusy}
        onResend={() => runBulk('bulk-resend', 'kuyruğa alındı')}
        onQueryGib={() => runBulk('bulk-query-gib', 'GİB sorgusuna alındı')}
        onClear={clearSelection}
      />
      <Pagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={pageSize}
        rowCount={rows.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
      />
    </>
  );

  return (
    <div style={s.container}>
      <EDocumentKpiStrip
        summary={summary}
        activeFilter={activeKpi}
        onSelect={(key) => { setActiveKpi(key); setCurrentPage(1); }}
      />

      <ListPageShell
        title="E-Belge Kontrol"
        subtitle="Belge elimizde, servisle GİB'e gidiyor — nerede takıldı? Servis hatası, zarf hatası ve karşı taraf yanıtı ayrı eksenlerdir."
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          // ══ SEÇİM ≠ DETAY (İŞ KURALI 8) ═══════════════════════════════════════════════════════
          // onRowClick BİLEREK BAĞLANMADI. Satıra tıklamak drawer AÇMAZ — açsaydı, çoklu seçim
          // yaparken her tıklamada panel açılır ve kullanıcı ne seçtiğini takip edemezdi.
          // Detay YALNIZ `İncele` ile açılır ve TAM 1 belge gösterir.
          onRowClick={undefined}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      <EDocumentDrawer
        open={drawerId != null}
        documentId={drawerId}
        onClose={() => setDrawerId(null)}
        onActionDone={refreshAll}
        showToast={showToast}
      />

      <EDocumentReconciliationModal
        open={reconOpen}
        onClose={() => setReconOpen(false)}
        showToast={showToast}
      />

      <Toast toast={toast} />
    </div>
  );
}

export default EDocumentControlPage;
